import { useCallback } from 'react'
import clsx from 'clsx'
import * as Toast from '@radix-ui/react-toast'
import { makeVar } from '@apollo/client'
import { useReactiveVar } from '@apollo/client/react'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'

import {
  Alert01Icon,
  InformationSquareIcon,
  Tick01Icon
} from '@hugeicons/core-solid-rounded'

export enum ToastType {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error'
}

export type ToastProps = {
  id: string
  type: ToastType
  message: string
  isBackground?: boolean
  duration?: number
  title?: string
  action?: {
    label: string
    icon?: HugeiconsIconProps['icon']
    onClick: () => void
  }
}

const TYPE_TO_ICON = {
  [ToastType.SUCCESS]: Tick01Icon,
  [ToastType.ERROR]: Alert01Icon,
  [ToastType.INFO]: InformationSquareIcon
}

const toastsVar = makeVar<ToastProps[]>([])

export function showToast(props: ToastProps) {
  const currentToasts = toastsVar()

  toastsVar([
    ...currentToasts,
    {
      ...props,
      // Adding timestamp so that ids are always unique
      id: `${props.id}-${Date.now()}`
    }
  ])
}

export function Toasts() {
  const toasts = useReactiveVar(toastsVar)

  const onOpenChange = useCallback(
    (id: string, open: boolean) => {
      if (!open) {
        // Give it time to disappear with an animation
        setTimeout(() => {
          toastsVar(toasts.filter(t => t.id !== id))
        }, 100)
      }
    },
    [toasts]
  )

  return toasts.map(function (toast) {
    return (
      <Toast.Root
        key={toast.id}
        duration={toast.duration ?? 5000}
        onOpenChange={open => onOpenChange(toast.id, open)}
        type={toast.isBackground ? 'background' : 'foreground'}
        className={clsx(
          'relative flex flex-row items-center rounded-small p-4 gap-2 justify-between animate-slide-in data-[state=closed]:animate-hide',
          toast.type === ToastType.INFO && 'bg-secondary text-white',
          toast.type === ToastType.SUCCESS && 'bg-green-500 text-white',
          toast.type === ToastType.ERROR && 'bg-red-500 text-white'
        )}
      >
        <HugeiconsIcon
          icon={TYPE_TO_ICON[toast.type]}
          className="size-6 text-white"
        />
        <div className="flex-1 flex flex-col gap-1">
          <div className="flex-1">
            {toast.title && (
              <Toast.Title className="text-heading3">{toast.title}</Toast.Title>
            )}
          </div>
          <Toast.Description className="text-regular">
            {toast.message}
          </Toast.Description>
        </div>
        {toast.action && (
          <Toast.Action asChild altText={toast.action.label}>
            <button
              tabIndex={0}
              onClick={toast.action.onClick}
              className="flex flex-row border-2 border-white text-white bg-transparent hover:bg-white/20 px-3 py-1 text-small rounded-small items-center gap-2"
            >
              {toast.action.icon && (
                <HugeiconsIcon icon={toast.action.icon} className="size-4" />
              )}
              <span>{toast.action.label}</span>
            </button>
          </Toast.Action>
        )}
      </Toast.Root>
    )
  })
}
